import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import type { RootState } from '../store';
import { getCurrentUser } from '../store/slices/authSlice';
import { useAppDispatch } from './useMedicalState';
import { isTokenExpired } from '../utils/tokenUtils';
import { expireSession } from '../utils/authSession';

/**
 * Hook to initialize authentication state on app load.
 * Validates token expiry client-side, then fetches the current user when needed.
 */
export const useAuthInitialization = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, isAuthenticated, isLoading, profile, accessibleRoutes } = useSelector(
    (state: RootState) => state.auth
  );
  const hasInitialized = useRef(false);
  const hasCheckedExpiry = useRef(false);

  useEffect(() => {
    if (hasCheckedExpiry.current) return;
    hasCheckedExpiry.current = true;

    if (token && isTokenExpired(token, 0)) {
      expireSession(dispatch, navigate, location);
      return;
    }
  }, [token, dispatch, navigate, location]);

  useEffect(() => {
    if (hasInitialized.current) return;

    if (token && isAuthenticated && !isLoading) {
      if (isTokenExpired(token, 0)) {
        expireSession(dispatch, navigate, location);
        return;
      }

      hasInitialized.current = true;
      dispatch(getCurrentUser() as any);
    }
  }, [token, isAuthenticated, isLoading, dispatch, navigate, location]);

  useEffect(() => {
    if (!token && !isAuthenticated) {
      hasInitialized.current = false;
      hasCheckedExpiry.current = false;
    }
  }, [token, isAuthenticated]);

  return {
    isInitialized: !isLoading && (!token || (token && user)),
    isLoading,
    isAuthenticated,
    user,
  };
};
